import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Papa from 'papaparse';

/**
 * Export transactions to PDF
 */
export const exportTransactionsToPDF = (transactions, language = 'fr') => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(18);
  doc?.text(language === 'fr' ? 'Rapport des Transactions' : 'تقرير المعاملات', 14, 20);
  
  // Date
  doc?.setFontSize(10);
  doc?.text(`Date: ${new Date()?.toLocaleDateString()}`, 14, 30);
  
  // Table headers
  const headers = language === 'fr' 
    ? [['Pèlerin', 'Montant', 'Type', 'Méthode', 'Statut', 'Date']]
    : [['الحاج', 'المبلغ', 'النوع', 'الطريقة', 'الحالة', 'التاريخ']];
  
  // Table data
  const data = transactions?.map(t => [
    t?.pilgrimName || '-',
    `${parseFloat(t?.amount || 0)?.toFixed(2)} MAD`,
    t?.type || '-',
    t?.method || '-',
    t?.status || '-',
    t?.transactionDate || t?.dueDate || '-'
  ]);
  
  doc?.autoTable({
    head: headers,
    body: data,
    startY: 40,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 128, 185] }
  });
  
  // Summary
  const totalAmount = transactions?.reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0);
  const finalY = doc?.lastAutoTable?.finalY || 40;
  doc?.setFontSize(12);
  doc?.text(
    language === 'fr' 
      ? `Total: ${totalAmount?.toFixed(2)} MAD`
      : `المجموع: ${totalAmount?.toFixed(2)} MAD`,
    14,
    finalY + 10
  );
  
  doc?.save(`transactions_${new Date()?.getTime()}.pdf`);
};

/**
 * Export transactions to CSV
 */
export const exportTransactionsToCSV = (transactions) => {
  const data = transactions?.map(t => ({
    'Pilgrim Name': t?.pilgrimName || '',
    'Pilgrim ID': t?.pilgrimId || '',
    'Amount': t?.amount || 0,
    'Type': t?.type || '',
    'Method': t?.method || '',
    'Status': t?.status || '',
    'Transaction Date': t?.transactionDate || '',
    'Due Date': t?.dueDate || '',
    'Group': t?.group || ''
  }));
  
  const csv = Papa?.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `transactions_${new Date()?.getTime()}.csv`;
  link?.click();
};

/**
 * Export pilgrims to PDF
 */
export const exportPilgrimsToPDF = (pilgrims, language = 'fr') => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(18);
  doc?.text(language === 'fr' ? 'Liste des Pèlerins' : 'قائمة الحجاج', 14, 20);
  
  // Date
  doc?.setFontSize(10);
  doc?.text(`Date: ${new Date()?.toLocaleDateString()}`, 14, 30);
  
  // Table headers
  const headers = language === 'fr'
    ? [['Nom', 'Passeport', 'Téléphone', 'Email', 'Visa', 'Documents', 'Paiement']]
    : [['الاسم', 'جواز السفر', 'الهاتف', 'البريد', 'التأشيرة', 'الوثائق', 'الدفع']];
  
  // Table data
  const data = pilgrims?.map(p => [
    p?.name || '-',
    p?.passportNumber || '-',
    p?.phone || '-',
    p?.email || '-',
    p?.visaStatus || '-',
    p?.documentStatus || '-',
    p?.paymentStatus || '-'
  ]);
  
  doc?.autoTable({
    head: headers,
    body: data,
    startY: 40,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 128, 185] }
  });
  
  // Summary
  const finalY = doc?.lastAutoTable?.finalY || 40;
  doc?.setFontSize(12);
  doc?.text(
    language === 'fr'
      ? `Total: ${pilgrims?.length} pèlerins`
      : `المجموع: ${pilgrims?.length} حاج`,
    14,
    finalY + 10
  );
  
  doc?.save(`pilgrims_${new Date()?.getTime()}.pdf`);
};

/**
 * Export pilgrims to CSV
 */
export const exportPilgrimsToCSV = (pilgrims) => {
  const data = pilgrims?.map(p => ({
    'Name': p?.name || '',
    'Passport Number': p?.passportNumber || '',
    'Phone': p?.phone || '',
    'Email': p?.email || '',
    'Group': p?.group || '',
    'Visa Status': p?.visaStatus || '',
    'Document Status': p?.documentStatus || '',
    'Payment Status': p?.paymentStatus || '',
    'Case Progress': p?.caseProgress || 0
  }));
  
  const csv = Papa?.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `pilgrims_${new Date()?.getTime()}.csv`;
  link?.click();
};

/**
 * Export profitability data to PDF
 */
export const exportProfitabilityToPDF = (groups, language = 'fr') => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(18);
  doc?.text(language === 'fr' ? 'Analyse de Rentabilité' : 'تحليل الربحية', 14, 20);
  
  // Date
  doc?.setFontSize(10);
  doc?.text(`Date: ${new Date()?.toLocaleDateString()}`, 14, 30);
  
  // Table headers
  const headers = language === 'fr'
    ? [['Groupe', 'Pèlerins', 'Revenus', 'Coûts', 'Profit', 'Marge %']]
    : [['المجموعة', 'الحجاج', 'الإيرادات', 'التكاليف', 'الربح', 'الهامش %']];
  
  // Table data
  const data = groups?.map(g => [
    g?.name || '-',
    g?.pilgrims || 0,
    `${parseFloat(g?.revenue || 0)?.toFixed(2)} MAD`,
    `${parseFloat(g?.costs || 0)?.toFixed(2)} MAD`,
    `${parseFloat(g?.profit || 0)?.toFixed(2)} MAD`,
    `${g?.margin || 0}%`
  ]);
  
  doc?.autoTable({
    head: headers,
    body: data,
    startY: 40,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 128, 185] }
  });
  
  // Summary
  const totalRevenue = groups?.reduce((sum, g) => sum + parseFloat(g?.revenue || 0), 0);
  const totalCosts = groups?.reduce((sum, g) => sum + parseFloat(g?.costs || 0), 0);
  const totalProfit = totalRevenue - totalCosts;
  const avgMargin = groups?.length > 0 ? groups?.reduce((sum, g) => sum + (g?.margin || 0), 0) / groups?.length : 0;
  
  const finalY = doc?.lastAutoTable?.finalY || 40;
  doc?.setFontSize(10);
  doc?.text(
    language === 'fr'
      ? `Revenus totaux: ${totalRevenue?.toFixed(2)} MAD`
      : `إجمالي الإيرادات: ${totalRevenue?.toFixed(2)} MAD`,
    14,
    finalY + 10
  );
  doc?.text(
    language === 'fr'
      ? `Coûts totaux: ${totalCosts?.toFixed(2)} MAD`
      : `إجمالي التكاليف: ${totalCosts?.toFixed(2)} MAD`,
    14,
    finalY + 18
  );
  doc?.text(
    language === 'fr'
      ? `Profit total: ${totalProfit?.toFixed(2)} MAD`
      : `إجمالي الربح: ${totalProfit?.toFixed(2)} MAD`,
    14,
    finalY + 26
  );
  doc?.text(
    language === 'fr'
      ? `Marge moyenne: ${avgMargin?.toFixed(2)}%`
      : `متوسط الهامش: ${avgMargin?.toFixed(2)}%`,
    14,
    finalY + 34
  );
  
  doc?.save(`profitability_${new Date()?.getTime()}.pdf`);
};

/**
 * Export analytics data to PDF
 */
export const exportAnalyticsToPDF = (analyticsData, dateRange, language = 'fr') => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(18);
  doc?.text(language === 'fr' ? 'Rapport d\'Analyse' : 'تقرير التحليلات', 14, 20);
  
  // Date range
  doc?.setFontSize(10);
  doc?.text(
    `${language === 'fr' ? 'Période' : 'الفترة'}: ${dateRange?.startDate} - ${dateRange?.endDate}`,
    14,
    30
  );
  
  // KPIs Section
  doc?.setFontSize(14);
  doc?.text(language === 'fr' ? 'Indicateurs Clés de Performance' : 'مؤشرات الأداء الرئيسية', 14, 45);
  
  doc?.setFontSize(10);
  let yPos = 55;
  
  const kpis = [
    {
      label: language === 'fr' ? 'Taux de conversion des pèlerins' : 'معدل تحويل الحجاج',
      value: `${analyticsData?.conversionRate || 0}%`
    },
    {
      label: language === 'fr' ? 'Revenu moyen par pèlerin' : 'متوسط الإيرادات لكل حاج',
      value: `${analyticsData?.avgRevenuePerPilgrim?.toLocaleString() || 0} MAD`
    },
    {
      label: language === 'fr' ? 'Taux de complétion des documents' : 'معدل إكمال الوثائق',
      value: `${analyticsData?.documentCompletionRate || 0}%`
    },
    {
      label: language === 'fr' ? 'Performance saisonnière' : 'الأداء الموسمي',
      value: analyticsData?.seasonalPerformance || 'N/A'
    }
  ];
  
  kpis?.forEach(kpi => {
    doc?.text(`${kpi?.label}: ${kpi?.value}`, 14, yPos);
    yPos += 8;
  });
  
  // Revenue Trend Table
  yPos += 10;
  doc?.setFontSize(14);
  doc?.text(language === 'fr' ? 'Tendance des Revenus' : 'اتجاه الإيرادات', 14, yPos);
  
  const revenueHeaders = language === 'fr'
    ? [['Mois', 'Revenus (MAD)', 'Pèlerins']]
    : [['الشهر', 'الإيرادات (MAD)', 'الحجاج']];
  
  const revenueData = analyticsData?.revenueTrend?.map(item => [
    item?.month || '-',
    item?.revenue?.toLocaleString() || '0',
    item?.pilgrims || '0'
  ]) || [];
  
  doc?.autoTable({
    head: revenueHeaders,
    body: revenueData,
    startY: yPos + 5,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 128, 185] }
  });
  
  // Performance Comparison
  const finalY = doc?.lastAutoTable?.finalY || yPos + 5;
  doc?.setFontSize(14);
  doc?.text(
    language === 'fr' ? 'Comparaison de Performance' : 'مقارنة الأداء',
    14,
    finalY + 15
  );
  
  const perfHeaders = language === 'fr'
    ? [['Catégorie', 'Période actuelle', 'Période précédente']]
    : [['الفئة', 'الفترة الحالية', 'الفترة السابقة']];
  
  const perfData = analyticsData?.performanceComparison?.map(item => [
    item?.category || '-',
    item?.current?.toLocaleString() || '0',
    item?.previous?.toLocaleString() || '0'
  ]) || [];
  
  doc?.autoTable({
    head: perfHeaders,
    body: perfData,
    startY: finalY + 20,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 128, 185] }
  });
  
  // Summary
  const summaryY = doc?.lastAutoTable?.finalY || finalY + 20;
  doc?.setFontSize(10);
  doc?.text(
    language === 'fr'
      ? `Généré le: ${new Date()?.toLocaleDateString()}`
      : `تم الإنشاء في: ${new Date()?.toLocaleDateString()}`,
    14,
    summaryY + 10
  );
  
  doc?.save(`analytics_report_${new Date()?.getTime()}.pdf`);
};

/**
 * Export analytics data to CSV
 */
export const exportAnalyticsToCSV = (analyticsData, dateRange) => {
  // KPIs Sheet
  const kpisData = [
    { Metric: 'Pilgrim Conversion Rate', Value: `${analyticsData?.conversionRate || 0}%` },
    { Metric: 'Avg Revenue Per Pilgrim', Value: `${analyticsData?.avgRevenuePerPilgrim || 0} MAD` },
    { Metric: 'Document Completion Rate', Value: `${analyticsData?.documentCompletionRate || 0}%` },
    { Metric: 'Seasonal Performance', Value: analyticsData?.seasonalPerformance || 'N/A' },
    { Metric: 'Date Range', Value: `${dateRange?.startDate} to ${dateRange?.endDate}` }
  ];
  
  // Revenue Trend Sheet
  const revenueTrendData = analyticsData?.revenueTrend?.map(item => ({
    Month: item?.month || '',
    Revenue: item?.revenue || 0,
    Pilgrims: item?.pilgrims || 0
  })) || [];
  
  // Performance Comparison Sheet
  const performanceData = analyticsData?.performanceComparison?.map(item => ({
    Category: item?.category || '',
    'Current Period': item?.current || 0,
    'Previous Period': item?.previous || 0,
    'Change': ((item?.current - item?.previous) / item?.previous * 100)?.toFixed(2) + '%'
  })) || [];
  
  // Conversion Funnel Sheet
  const conversionData = analyticsData?.conversionData?.map(item => ({
    Stage: item?.stage || '',
    'Conversion Rate': `${item?.rate || 0}%`
  })) || [];
  
  // Combine all data
  const combinedData = [
    { Section: 'KEY PERFORMANCE INDICATORS' },
    ...kpisData,
    { Section: '' },
    { Section: 'REVENUE TREND' },
    ...revenueTrendData,
    { Section: '' },
    { Section: 'PERFORMANCE COMPARISON' },
    ...performanceData,
    { Section: '' },
    { Section: 'CONVERSION FUNNEL' },
    ...conversionData
  ];
  
  const csv = Papa?.unparse(combinedData);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `analytics_data_${new Date()?.getTime()}.csv`;
  link?.click();
};

/**
 * Export ROI analysis data to PDF
 */
export const exportROIAnalysisToPDF = (roiData, language = 'fr') => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(18);
  doc?.text(language === 'fr' ? 'Analyse ROI - Rapport de Rentabilité' : 'تحليل العائد على الاستثمار - تقرير الربحية', 14, 20);
  
  // Date
  doc?.setFontSize(10);
  doc?.text(`Date: ${new Date()?.toLocaleDateString()}`, 14, 30);
  
  // Calculate summary metrics
  const totalRevenue = roiData?.groups?.reduce((sum, g) => sum + parseFloat(g?.revenue || 0), 0) || 0;
  const totalCosts = roiData?.groups?.reduce((sum, g) => sum + parseFloat(g?.costs || 0), 0) || 0;
  const totalProfit = totalRevenue - totalCosts;
  const totalROI = totalCosts > 0 ? ((totalProfit / totalCosts) * 100) : 0;
  const avgMargin = roiData?.groups?.length > 0 
    ? roiData?.groups?.reduce((sum, g) => sum + (parseFloat(g?.margin || 0)), 0) / roiData?.groups?.length 
    : 0;
  const profitableGroups = roiData?.groups?.filter(g => parseFloat(g?.profit || 0) > 0)?.length || 0;
  
  // Summary Section
  doc?.setFontSize(14);
  doc?.text(language === 'fr' ? 'Résumé Financier' : 'الملخص المالي', 14, 40);
  
  doc?.setFontSize(10);
  let yPos = 50;
  
  const summaryMetrics = [
    {
      label: language === 'fr' ? 'ROI Total' : 'إجمالي العائد',
      value: `${totalROI?.toFixed(2)}%`
    },
    {
      label: language === 'fr' ? 'Marge Moyenne' : 'متوسط الهامش',
      value: `${avgMargin?.toFixed(2)}%`
    },
    {
      label: language === 'fr' ? 'Revenus Totaux' : 'إجمالي الإيرادات',
      value: `${totalRevenue?.toFixed(2)} MAD`
    },
    {
      label: language === 'fr' ? 'Coûts Totaux' : 'إجمالي التكاليف',
      value: `${totalCosts?.toFixed(2)} MAD`
    },
    {
      label: language === 'fr' ? 'Profit Total' : 'إجمالي الربح',
      value: `${totalProfit?.toFixed(2)} MAD`
    },
    {
      label: language === 'fr' ? 'Groupes Rentables' : 'المجموعات المربحة',
      value: `${profitableGroups}/${roiData?.groups?.length || 0}`
    }
  ];
  
  summaryMetrics?.forEach(metric => {
    doc?.text(`${metric?.label}: ${metric?.value}`, 14, yPos);
    yPos += 8;
  });
  
  // Group Rankings Table
  yPos += 10;
  doc?.setFontSize(14);
  doc?.text(language === 'fr' ? 'Classement ROI des Groupes' : 'تصنيف العائد للمجموعات', 14, yPos);
  
  const headers = language === 'fr'
    ? [['Rang', 'Groupe', 'ROI %', 'Marge %', 'Revenus', 'Coûts', 'Profit']]
    : [['الترتيب', 'المجموعة', 'العائد %', 'الهامش %', 'الإيرادات', 'التكاليف', 'الربح']];
  
  // Sort groups by ROI
  const sortedGroups = [...(roiData?.groups || [])]
    ?.map(g => {
      const revenue = parseFloat(g?.revenue || 0);
      const costs = parseFloat(g?.costs || 0);
      const profit = parseFloat(g?.profit || 0);
      const roi = costs > 0 ? ((profit / costs) * 100) : 0;
      return { ...g, roi };
    })
    ?.sort((a, b) => b?.roi - a?.roi);
  
  const data = sortedGroups?.map((g, index) => [
    `#${index + 1}`,
    g?.name?.substring(0, 20) || '-',
    `${g?.roi?.toFixed(1)}%`,
    `${g?.margin?.toFixed(1)}%`,
    `${parseFloat(g?.revenue || 0)?.toFixed(0)} MAD`,
    `${parseFloat(g?.costs || 0)?.toFixed(0)} MAD`,
    `${parseFloat(g?.profit || 0)?.toFixed(0)} MAD`
  ]);
  
  doc?.autoTable({
    head: headers,
    body: data,
    startY: yPos + 5,
    styles: { fontSize: 7 },
    headStyles: { fillColor: [41, 128, 185] },
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 40 },
      2: { cellWidth: 20 },
      3: { cellWidth: 20 },
      4: { cellWidth: 30 },
      5: { cellWidth: 30 },
      6: { cellWidth: 30 }
    }
  });
  
  // Footer
  const finalY = doc?.lastAutoTable?.finalY || yPos + 5;
  doc?.setFontSize(8);
  doc?.text(
    language === 'fr'
      ? `Généré le: ${new Date()?.toLocaleString()}`
      : `تم الإنشاء في: ${new Date()?.toLocaleString()}`,
    14,
    finalY + 10
  );
  
  doc?.save(`roi_analysis_${new Date()?.getTime()}.pdf`);
};

/**
 * Export ROI analysis data to CSV
 */
export const exportROIAnalysisToCSV = (roiData) => {
  // Calculate ROI for each group
  const groupsWithROI = roiData?.groups?.map(g => {
    const revenue = parseFloat(g?.revenue || 0);
    const costs = parseFloat(g?.costs || 0);
    const profit = parseFloat(g?.profit || 0);
    const roi = costs > 0 ? ((profit / costs) * 100) : 0;
    const margin = revenue > 0 ? ((profit / revenue) * 100) : 0;
    
    return {
      'Group Name': g?.name || '',
      'Pilgrims': g?.pilgrims || 0,
      'Revenue (MAD)': revenue?.toFixed(2),
      'Costs (MAD)': costs?.toFixed(2),
      'Profit (MAD)': profit?.toFixed(2),
      'ROI (%)': roi?.toFixed(2),
      'Margin (%)': margin?.toFixed(2),
      'Departure Date': g?.departureDate || '',
      'Type': g?.type || ''
    };
  }) || [];
  
  // Sort by ROI descending
  groupsWithROI?.sort((a, b) => parseFloat(b?.['ROI (%)']) - parseFloat(a?.['ROI (%)']));
  
  // Add summary row
  const totalRevenue = roiData?.groups?.reduce((sum, g) => sum + parseFloat(g?.revenue || 0), 0) || 0;
  const totalCosts = roiData?.groups?.reduce((sum, g) => sum + parseFloat(g?.costs || 0), 0) || 0;
  const totalProfit = totalRevenue - totalCosts;
  const totalROI = totalCosts > 0 ? ((totalProfit / totalCosts) * 100) : 0;
  
  const summaryRow = {
    'Group Name': 'TOTAL',
    'Pilgrims': roiData?.groups?.reduce((sum, g) => sum + parseInt(g?.pilgrims || 0), 0),
    'Revenue (MAD)': totalRevenue?.toFixed(2),
    'Costs (MAD)': totalCosts?.toFixed(2),
    'Profit (MAD)': totalProfit?.toFixed(2),
    'ROI (%)': totalROI?.toFixed(2),
    'Margin (%)': totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100)?.toFixed(2) : '0.00',
    'Departure Date': '',
    'Type': ''
  };
  
  groupsWithROI?.push(summaryRow);
  
  const csv = Papa?.unparse(groupsWithROI);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `roi_analysis_${new Date()?.getTime()}.csv`;
  link?.click();
};

/**
 * Export breakeven analysis to PDF
 */
export const exportBreakevenAnalysisToPDF = (breakevenData, groupName, language = 'fr') => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(18);
  doc?.text(
    language === 'fr' ?'Analyse du Seuil de Rentabilité' :'تحليل نقطة التعادل',
    14,
    20
  );
  
  // Group name
  doc?.setFontSize(12);
  doc?.text(
    `${language === 'fr' ? 'Groupe' : 'المجموعة'}: ${groupName}`,
    14,
    30
  );
  
  // Date
  doc?.setFontSize(10);
  doc?.text(`Date: ${new Date()?.toLocaleDateString()}`, 14, 38);
  
  // Key Metrics
  doc?.setFontSize(14);
  doc?.text(
    language === 'fr' ? 'Métriques Clés' : 'المقاييس الرئيسية',
    14,
    50
  );
  
  doc?.setFontSize(10);
  let yPos = 60;
  
  const metrics = [
    {
      label: language === 'fr' ? 'Seuil de rentabilité' : 'نقطة التعادل',
      value: `${breakevenData?.breakevenPilgrims} ${language === 'fr' ? 'pèlerins' : 'حجاج'}`
    },
    {
      label: language === 'fr' ? 'Revenu au seuil' : 'الإيراد عند التعادل',
      value: `${breakevenData?.breakevenRevenue?.toFixed(2)} MAD`
    },
    {
      label: language === 'fr' ? 'Coûts fixes' : 'التكاليف الثابتة',
      value: `${breakevenData?.fixedCosts?.toFixed(2)} MAD`
    },
    {
      label: language === 'fr' ? 'Coût variable par pèlerin' : 'التكلفة المتغيرة لكل حاج',
      value: `${breakevenData?.variableCostPerPilgrim?.toFixed(2)} MAD`
    },
    {
      label: language === 'fr' ? 'Revenu par pèlerin' : 'الإيراد لكل حاج',
      value: `${breakevenData?.revenuePerPilgrim?.toFixed(2)} MAD`
    },
    {
      label: language === 'fr' ? 'Marge de contribution' : 'هامش المساهمة',
      value: `${breakevenData?.contributionMargin?.toFixed(2)} MAD`
    },
    {
      label: language === 'fr' ? 'Pèlerins actuels' : 'عدد الحجاج الحالي',
      value: `${breakevenData?.currentPilgrims}`
    },
    {
      label: language === 'fr' ? 'Marge de sécurité' : 'هامش الأمان',
      value: `${breakevenData?.safetyMargin?.toFixed(2)}%`
    }
  ];
  
  metrics?.forEach(metric => {
    doc?.text(`${metric?.label}: ${metric?.value}`, 14, yPos);
    yPos += 8;
  });
  
  // Insights
  yPos += 10;
  doc?.setFontSize(14);
  doc?.text(language === 'fr' ? 'Insights' : 'رؤى', 14, yPos);
  
  doc?.setFontSize(9);
  yPos += 10;
  
  const insights = [
    language === 'fr'
      ? `Vous devez avoir au moins ${breakevenData?.breakevenPilgrims} pèlerins pour couvrir tous les coûts.`
      : `يجب أن يكون لديك على الأقل ${breakevenData?.breakevenPilgrims} حاج لتغطية جميع التكاليف.`,
    language === 'fr'
      ? `Actuellement, vous avez ${breakevenData?.currentPilgrims} pèlerins, ce qui vous donne une marge de sécurité de ${breakevenData?.safetyMargin?.toFixed(1)}%.`
      : `حاليًا، لديك ${breakevenData?.currentPilgrims} حاج، مما يمنحك هامش أمان ${breakevenData?.safetyMargin?.toFixed(1)}%.`,
    language === 'fr'
      ? `Chaque pèlerin supplémentaire au-delà du seuil génère ${breakevenData?.contributionMargin?.toFixed(2)} MAD de profit.`
      : `كل حاج إضافي بعد نقطة التعادل يولد ${breakevenData?.contributionMargin?.toFixed(2)} درهم من الربح.`
  ];
  
  insights?.forEach(insight => {
    const lines = doc?.splitTextToSize(insight, 180);
    doc?.text(lines, 14, yPos);
    yPos += lines?.length * 6;
  });
  
  doc?.save(`breakeven_analysis_${groupName?.replace(/\s+/g, '_')}_${new Date()?.getTime()}.pdf`);
};

export default {
  exportTransactionsToPDF,
  exportTransactionsToCSV,
  exportPilgrimsToPDF,
  exportPilgrimsToCSV,
  exportProfitabilityToPDF,
  exportAnalyticsToPDF,
  exportAnalyticsToCSV,
  exportROIAnalysisToPDF,
  exportROIAnalysisToCSV,
  exportBreakevenAnalysisToPDF
};